/**
 * Editing a placeholder chip in place: clicking one (or Enter while it is selected) asks the page
 * to open PlaceholderPopover for it, and applyPlaceholderEdit writes the result back as a single
 * transaction with its own history step.
 */
import { Extension, type Editor } from '@tiptap/core'
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state'
import { closeHistory } from '@tiptap/pm/history'
import { normalizeMonikerKey } from './monikerKey'
import type { FormatterSpec } from './types'

type EditRequest = (pos: number) => void
const editRequests = new WeakMap<Editor, EditRequest>()

export function setPlaceholderEditHandler(editor: Editor, handler: EditRequest | null) {
  if (handler) editRequests.set(editor, handler)
  else editRequests.delete(editor)
}

export interface PlaceholderEdit {
  rawName: string
  formatters: FormatterSpec[]
}

/** Why a field name cannot be used, or null. Mirrors what the input rule refuses to turn into a chip. */
export function placeholderNameError(rawName: string): string | null {
  const t = rawName.trim()
  if (!t) return 'Enter a field name.'
  if (/[{}|]/.test(t)) return 'Field names cannot contain braces or |.'
  if (t.startsWith('#') || t.startsWith('/') || t === 'else') return 'That is a control tag, not a field.'
  return null
}

function sameFormatters(a: FormatterSpec[] | null, b: FormatterSpec[] | null): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

export function applyPlaceholderEdit(editor: Editor, pos: number, edit: PlaceholderEdit): boolean {
  const { state, view } = editor
  const node = state.doc.nodeAt(pos)
  if (!node || node.type.name !== 'placeholder' || placeholderNameError(edit.rawName)) return false
  const rawName = edit.rawName.trim()
  const formatters = edit.formatters.length ? edit.formatters.map(f => ({ name: f.name, args: [...f.args] })) : null
  if (rawName === node.attrs.rawName && sameFormatters(formatters, node.attrs.formatters as FormatterSpec[] | null)) return true
  const tr = closeHistory(state.tr)
  tr.setNodeMarkup(pos, undefined, { ...node.attrs, rawName, normalizedKey: normalizeMonikerKey(rawName), formatters })
  tr.setSelection(NodeSelection.create(tr.doc, pos))
  view.dispatch(tr)
  return true
}

export const PlaceholderEditing = Extension.create({
  name: 'placeholderEditing',
  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey('placeholderEditing'),
        props: {
          handleClickOn(_view, _pos, node, nodePos, event) {
            if (node.type.name !== 'placeholder' || !editor.isEditable || event.button !== 0) return false
            const request = editRequests.get(editor)
            if (request) window.setTimeout(() => request(nodePos))
            return false
          },
          handleKeyDown(view, event) {
            if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return false
            const sel = view.state.selection
            if (!(sel instanceof NodeSelection) || sel.node.type.name !== 'placeholder') return false
            const request = editRequests.get(editor)
            if (!request || !editor.isEditable) return false
            request(sel.from)
            return true
          },
        },
      }),
    ]
  },
})
